import type { Locale, OperationalPaymentContract } from "@chams-pay/contracts";
import { documentMessages, operationsMessages, translate } from "@chams-pay/contracts";
import { FinancialDocument, printFinancialDocument } from "@chams-pay/document-ui";
import {
  ActionsButton,
  ActionsGroup,
  ActionsLabel,
  Block,
  Button,
  Page,
  Popup,
  Segmented,
  SegmentedButton,
} from "konsta/react";
import { useState } from "react";
import { casablancaDate } from "../core/format.js";
import type { OperationalPaymentController } from "../core/use-operations.js";
import { useOperationalPayment } from "../core/use-operations.js";
import { DatePicker } from "../ui/date-picker.js";
import { Amount, Bidi, DateValue } from "../ui/figures.js";
import { MobileA4DocumentPreview } from "./a4-document-preview.js";
import { ScopedError } from "../ui/states.js";
import { showMobileFeedback, useAppBackClose } from "./app-feedback.js";
import { MobileDraggableActions } from "./draggable-sheet.js";
import { MobileSurface } from "./surfaces.js";
import { MobileWhatsAppActions } from "./whatsapp-actions.js";

/* ------------------------------------------------------------------ */
/* Synchronized payment: document, sharing and cancellation            */
/* ------------------------------------------------------------------ */

const lifecycleKey = {
  pending: "ops.pending",
  settled: "ops.settled",
  rejected: "ops.rejected",
  cancelled: "ops.cancelled",
} as const satisfies Record<
  OperationalPaymentContract["lifecycle"],
  Parameters<typeof translate>[1]
>;

function CancelPopup({
  locale,
  payment,
  controller,
}: {
  readonly locale: Locale;
  readonly payment: OperationalPaymentContract;
  readonly controller: OperationalPaymentController;
}) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const blocked = controller.cancelDisabled || controller.cancellationDate.length === 0;
  useAppBackClose(controller.showCancellation, () => controller.setShowCancellation(false));

  /* A full popup, never a sheet: the destructive decision owns the screen and
     is dismissed only by an explicit choice. */
  return (
    <Popup
      opened={controller.showCancellation}
      size="w-screen"
      className="mobile-workspace-popup"
      data-testid="payment-cancel-dialog"
    >
      <Page className="chams-page mobile-workspace-popup-page bg-canvas">
        <div className="flex items-start gap-3 border-b border-destructive-border bg-destructive-soft p-5">
          <span
            aria-hidden="true"
            className="grid size-9 shrink-0 place-items-center rounded-full bg-destructive font-heading text-base font-extrabold text-white"
          >
            !
          </span>
          <span className="flex flex-col gap-1">
            <strong className="font-heading text-base text-destructive">
              {t("ops.cancelTitle")}
            </strong>
            <span className="text-[11.5px] leading-relaxed text-destructive">
              {t("ops.cancelWarn")}
            </span>
          </span>
        </div>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            void controller.cancelPayment();
          }}
        >
          <Block strong inset className="flex flex-col gap-2 text-[11.5px]">
            <span className="flex justify-between gap-3">
              <span className="text-muted-foreground">{t("ops.reference")}</span>
              <Bidi className="font-bold">{payment.payment.reference}</Bidi>
            </span>
            <span className="flex justify-between gap-3">
              <span className="text-muted-foreground">{t("ops.amount")}</span>
              <Amount value={payment.payment.amountMad} locale={locale} className="font-bold" />
            </span>
            <span className="flex justify-between gap-3">
              <span className="text-muted-foreground">{t("ops.student")}</span>
              <span className="font-heading font-bold">
                {locale === "ar"
                  ? payment.document.student.nameAr
                  : payment.document.student.nameFr}
              </span>
            </span>
          </Block>

          <Block strong inset className="flex flex-col gap-4 !bg-white">
            <DatePicker
              locale={locale}
              label={t("ops.cancelDate")}
              showLabel
              max={casablancaDate()}
              value={controller.cancellationDate}
              onChange={controller.setCancellationDate}
            />
            <label className="flex flex-col gap-1.5">
              <span className="font-heading text-[11px] font-semibold text-muted-foreground">
                {t("ops.cancelReason")}
              </span>
              <textarea
                required
                minLength={3}
                maxLength={500}
                value={controller.cancellationReason}
                className="min-h-28 w-full resize-y rounded-2xl border border-input bg-white px-3.5 py-2.5 text-sm text-foreground outline-none"
                onChange={(event) => controller.setCancellationReason(event.target.value)}
              />
            </label>
          </Block>

          <Block className="cancellation-confirmation flex min-h-14 items-center gap-3 !bg-white text-xs font-semibold leading-relaxed">
            <input
              type="checkbox"
              className="size-6 shrink-0 accent-[rgb(var(--primary))]"
              aria-label={t("ops.confirmCancellationCheck")}
              checked={controller.confirmed}
              onChange={(event) => controller.setConfirmed(event.target.checked)}
            />
            <span>{t("ops.confirmCancellationCheck")}</span>
          </Block>

          {controller.actionFailed && (
            <Block>
              <ScopedError locale={locale} onRetry={() => void controller.cancelPayment()} />
            </Block>
          )}

          <Block className="flex flex-col gap-2 pb-8">
            <Button
              component="button"
              type="submit"
              large
              rounded
              className="min-h-12 !bg-destructive font-heading font-bold !text-white disabled:!bg-disabled"
              disabled={blocked}
            >
              {t("ops.confirmCancel")}
            </Button>
            <Button
              component="button"
              type="button"
              large
              rounded
              clear
              className="min-h-12"
              onClick={() => controller.setShowCancellation(false)}
            >
              {t("ops.keepPayment")}
            </Button>
          </Block>
        </form>
      </Page>
    </Popup>
  );
}

export function MobileOperationalPaymentPopup({
  locale,
  payment,
  csrf,
  onClose,
  onChanged,
}: {
  readonly locale: Locale;
  readonly payment: OperationalPaymentContract | null;
  readonly csrf: string;
  readonly onClose: () => void;
  readonly onChanged: () => void;
}) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const text = operationsMessages(locale);
  const controller = useOperationalPayment({ payment, csrf, onClose, onChanged });
  const documentText = documentMessages(controller.receiptLocale);
  const [sheet, setSheet] = useState(false);
  const cancelled = payment?.lifecycle === "cancelled";

  return (
    <>
      <MobileSurface
        opened={payment !== null}
        title={payment?.payment.reference ?? t("ops.viewDocument")}
        closeLabel={t("common.close")}
        testId="operational-payment-surface"
        onClose={onClose}
      >
        {payment && (
          <div className="payment-history-surface pb-8">
            <Block className="payment-lifecycle-line !my-3 flex flex-wrap items-center gap-3">
              <Bidi
                className={`font-heading text-base font-extrabold ${
                  cancelled ? "text-muted-foreground line-through" : ""
                }`}
              >
                {payment.payment.reference}
              </Bidi>
              <span
                className={`inline-flex h-6 items-center gap-1.5 rounded-full px-2.5 font-heading text-[11px] font-bold ${
                  payment.lifecycle === "settled"
                    ? "bg-success-soft text-success"
                    : payment.lifecycle === "pending"
                      ? "bg-warning-soft text-warning"
                      : "bg-destructive-soft text-destructive"
                }`}
              >
                <span aria-hidden="true" className="size-1.5 rounded-full bg-current" />
                {t(lifecycleKey[payment.lifecycle])}
              </span>
              <Amount
                value={payment.payment.amountMad}
                locale={locale}
                className="ms-auto text-lg font-extrabold"
              />
            </Block>

            <Block className="!my-2">
              <Segmented
                strong
                className="mobile-segmented receipt-language no-print"
                data-tab-count="2"
                data-active-index={controller.receiptLocale === "ar" ? "0" : "1"}
              >
                <SegmentedButton
                  active={controller.receiptLocale === "ar"}
                  className="min-h-11"
                  onClick={() => controller.setReceiptLocale("ar")}
                >
                  {documentText.arabic}
                </SegmentedButton>
                <SegmentedButton
                  active={controller.receiptLocale === "fr"}
                  className="min-h-11"
                  onClick={() => controller.setReceiptLocale("fr")}
                >
                  {documentText.french}
                </SegmentedButton>
              </Segmented>
            </Block>

            <Block className="flex flex-col gap-2">
              <Button
                component="button"
                type="button"
                large
                rounded
                className="min-h-12"
                onClick={() => setSheet(true)}
              >
                {t("ops.actions")}
              </Button>
              {controller.shareable && !cancelled && (
                <MobileWhatsAppActions
                  whatsapp={controller.whatsapp}
                  canPrepare={Boolean(csrf)}
                  text={{
                    send: text.whatsapp,
                    generate: text.whatsappGenerate,
                    preparing: text.whatsappPreparing,
                    copy: text.whatsappCopy,
                    copied: text.whatsappCopied,
                    prepareFailed: text.whatsappPrepareFailed,
                    retry: text.whatsappRetry,
                    copyFailed: text.whatsappCopyFailed,
                  }}
                  onCopied={() => showMobileFeedback("copied")}
                />
              )}
            </Block>

            <div className="bg-sunken p-3">
              <div className="animate-rise">
                <MobileA4DocumentPreview locale={controller.receiptLocale}>
                  <FinancialDocument
                    locale={controller.receiptLocale}
                    snapshot={payment.document}
                    lifecycle={payment.lifecycle}
                    {...(payment.settlementDate ? { settlementDate: payment.settlementDate } : {})}
                    {...(payment.cancellation
                      ? { cancellationReason: payment.cancellation.reason }
                      : {})}
                  />
                </MobileA4DocumentPreview>
              </div>
            </div>

            {payment.cancellation && (
              <Block
                strong
                inset
                className="cancellation-audit-context flex flex-col gap-1 text-[11.5px] text-destructive"
              >
                <strong className="font-heading">{t("ops.cancelledOn")}</strong>
                <DateValue value={payment.cancellation.date} />
                <span>
                  {t("ops.cancelledBy")}{" "}
                  {locale === "ar"
                    ? payment.cancellation.actorNameAr
                    : payment.cancellation.actorNameFr}
                </span>
                <span>{payment.cancellation.reason}</span>
              </Block>
            )}

            {controller.actionFailed && !controller.showCancellation && (
              <Block>
                <ScopedError locale={locale} />
              </Block>
            )}
          </div>
        )}
      </MobileSurface>

      {payment && sheet && (
        <button
          type="button"
          aria-label={t("common.close")}
          className="receipt-actions-backdrop fixed inset-0 z-[55] bg-black/40"
          onClick={() => setSheet(false)}
        />
      )}

      {payment && (
        <MobileDraggableActions
          opened={sheet}
          onClose={() => setSheet(false)}
          backdrop={false}
          className="mobile-workspace-actions receipt-history-actions"
        >
          <ActionsGroup>
            <ActionsLabel>{payment.payment.reference}</ActionsLabel>
            <ActionsButton
              bold
              onClick={() => {
                setSheet(false);
                printFinancialDocument();
              }}
            >
              {t("ops.print")}
            </ActionsButton>
            {controller.official && (
              <ActionsButton
                onClick={() => {
                  setSheet(false);
                  void controller.download();
                  showMobileFeedback("download-started");
                }}
              >
                {t("ops.download")}
              </ActionsButton>
            )}
            {payment.canCancel && (
              <ActionsButton
                colors={{ textIos: "text-destructive", textMaterial: "text-destructive" }}
                onClick={() => {
                  setSheet(false);
                  controller.setShowCancellation(true);
                }}
              >
                {t("ops.cancelPayment")}
              </ActionsButton>
            )}
          </ActionsGroup>
        </MobileDraggableActions>
      )}

      {payment && payment.canCancel && (
        <CancelPopup locale={locale} payment={payment} controller={controller} />
      )}
    </>
  );
}
